// Merges the built-in CATEGORIES (from data/catalog.js, fixed at build time)
// with categories/subcategories/themes created later through Photo Studio,
// applies the owner's deletions (hidden built-ins + removed custom items),
// and attaches per-subcategory tile images. All persisted in the backend.
import axios from "axios";
import { CATEGORIES, PRODUCT_TYPES, COLLECTIONS, DEFAULT_SUB_IMAGES } from "../data/catalog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

let customCategories = [];
let extraSubsByCategory = {};
let customThemes = [];
let hiddenCategories = new Set();
let hiddenSubs = new Set();      // keys "catSlug/subSlug"
let hiddenCollections = new Set();
let subImages = {};              // { "catSlug/subSlug": imageId }
let themeImages = {};            // { themeSlug: imageId }
let loaded = false;
let loadingPromise = null;

export function isDynamicCatalogLoaded() {
    return loaded;
}

export function catalogImageUrl(imageId) {
    return imageId ? `${API}/catalog/image/${imageId}` : null;
}

export function getSubcategoryImage(catSlug, subSlug) {
    const uploaded = catalogImageUrl(subImages[`${catSlug}/${subSlug}`]);
    return uploaded || DEFAULT_SUB_IMAGES[`${catSlug}/${subSlug}`] || null;
}

export function getThemeImage(themeSlug) {
    return catalogImageUrl(themeImages[themeSlug]) || null;
}

export async function refreshDynamicCategories() {
    const run = async () => {
        try {
            const { data } = await axios.get(`${API}/categories/custom`);
            customCategories = (data?.categories || []).map((c) => ({
                slug: c.slug,
                name: c.name,
                tagline: c.tagline || "Custom built, just for you.",
                description: c.description || "",
                custom: true,
            }));
            const byCat = {};
            for (const s of data?.subcategories || []) {
                (byCat[s.category_slug] = byCat[s.category_slug] || []).push({
                    slug: s.slug,
                    name: s.name,
                    blurb: s.blurb || "",
                    productType: s.product_type || PRODUCT_TYPES.SINGLE_ITEM,
                    custom: true,
                });
            }
            extraSubsByCategory = byCat;
            hiddenCategories = new Set(data?.hidden_categories || []);
            hiddenSubs = new Set((data?.hidden_subcategories || []).map((h) => `${h.parent}/${h.slug}`));
            subImages = data?.subcategory_images || {};

            try {
                const tres = await axios.get(`${API}/collections/custom`);
                customThemes = (tres.data?.collections || []).map((t) => ({
                    slug: t.slug, name: t.name, custom: true,
                }));
                hiddenCollections = new Set(tres.data?.hidden_collections || []);
                themeImages = tres.data?.collection_images || {};
            } catch {
                customThemes = customThemes || [];
            }
        } catch (e) {
            customCategories = customCategories || [];
            extraSubsByCategory = extraSubsByCategory || {};
        } finally {
            loaded = true;
        }
    };
    if (!loadingPromise) loadingPromise = run().finally(() => { loadingPromise = null; });
    return loadingPromise;
}

// All categories — built-in + custom — minus any hidden ones, each with its
// subcategories (built-in + custom, minus hidden) and tile images attached.
export function getAllCategories() {
    const build = (c, isCustom) => {
        const subs = [...(c.sub || []), ...(extraSubsByCategory[c.slug] || [])]
            .filter((s) => !hiddenSubs.has(`${c.slug}/${s.slug}`))
            .map((s) => ({ ...s, image: getSubcategoryImage(c.slug, s.slug) }));
        return { ...c, custom: isCustom, sub: subs };
    };
    const merged = CATEGORIES
        .filter((c) => !hiddenCategories.has(c.slug))
        .map((c) => build(c, false));
    for (const cc of customCategories) {
        if (hiddenCategories.has(cc.slug)) continue;
        if (merged.find((m) => m.slug === cc.slug)) continue;
        merged.push(build({ ...cc, sub: [] }, true));
    }
    return merged;
}

export function findCategoryMerged(slug) {
    if (!slug) return null;
    return getAllCategories().find((c) => c.slug === slug) || null;
}

export function findSubMerged(catSlug, subSlug) {
    const cat = findCategoryMerged(catSlug);
    if (!cat) return null;
    return cat.sub.find((s) => s.slug === subSlug) || null;
}

export function getProductTypeMerged(catSlug, subSlug) {
    const sub = findSubMerged(catSlug, subSlug);
    if (sub?.productType) return sub.productType;
    const cat = findCategoryMerged(catSlug);
    if (cat?.productType) return cat.productType;
    return PRODUCT_TYPES.SINGLE_ITEM;
}

export async function createCategory(name) {
    const existing_slugs = getAllCategories().map((c) => c.slug);
    const { data } = await axios.post(`${API}/categories`, { name, existing_slugs });
    await refreshDynamicCategories();
    return data;
}

export async function createSubcategory(categorySlug, name) {
    const cat = findCategoryMerged(categorySlug);
    const existing_slugs = (cat?.sub || []).map((s) => s.slug);
    const { data } = await axios.post(`${API}/categories/${categorySlug}/subcategories`, { name, existing_slugs });
    await refreshDynamicCategories();
    return data;
}

// All themes/collections — built-in + custom — minus hidden ones, each with its image.
export function getAllThemes() {
    const merged = COLLECTIONS.filter((c) => !hiddenCollections.has(c.slug))
        .map((c) => ({ ...c, image: getThemeImage(c.slug) }));
    for (const t of customThemes) {
        if (hiddenCollections.has(t.slug)) continue;
        if (merged.find((m) => m.slug === t.slug)) continue;
        merged.push({ ...t, image: getThemeImage(t.slug) });
    }
    return merged;
}

export async function createTheme(name) {
    const existing_slugs = getAllThemes().map((t) => t.slug);
    const { data } = await axios.post(`${API}/collections`, { name, existing_slugs });
    await refreshDynamicCategories();
    return data;
}

// ---- Deletes (custom items are removed; built-ins are hidden) ----
export async function deleteCategory(slug) {
    await axios.delete(`${API}/categories/${slug}`);
    await refreshDynamicCategories();
}
export async function deleteSubcategory(catSlug, subSlug) {
    await axios.delete(`${API}/categories/${catSlug}/subcategories/${subSlug}`);
    await refreshDynamicCategories();
}
export async function deleteTheme(slug) {
    await axios.delete(`${API}/collections/${slug}`);
    await refreshDynamicCategories();
}

// ---- Subcategory tile image upload ----
export async function uploadSubcategoryImage(catSlug, subSlug, file) {
    const fd = new FormData();
    fd.append("image", file);
    const { data } = await axios.post(
        `${API}/categories/${catSlug}/subcategories/${subSlug}/image`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
    );
    await refreshDynamicCategories();
    return data;
}

// ---- Theme tile image upload ----
export async function uploadThemeImage(themeSlug, file) {
    const fd = new FormData();
    fd.append("image", file);
    const { data } = await axios.post(
        `${API}/collections/${themeSlug}/image`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
    );
    await refreshDynamicCategories();
    return data;
}

// ---- Admin "Manage" views: include hidden items, flagged (not filtered out) ----
export function getManageCategories() {
    const annotate = (c, isCustom) => {
        const subs = [...(c.sub || []), ...(extraSubsByCategory[c.slug] || [])].map((s) => ({
            ...s,
            hidden: hiddenSubs.has(`${c.slug}/${s.slug}`),
            image: getSubcategoryImage(c.slug, s.slug),
        }));
        return { ...c, custom: isCustom, hidden: hiddenCategories.has(c.slug), sub: subs };
    };
    const merged = CATEGORIES.map((c) => annotate(c, false));
    for (const cc of customCategories) {
        if (merged.find((m) => m.slug === cc.slug)) continue;
        merged.push(annotate({ ...cc, sub: [] }, true));
    }
    return merged;
}

export function getManageThemes() {
    const merged = COLLECTIONS.map((c) => ({ ...c, hidden: hiddenCollections.has(c.slug), image: getThemeImage(c.slug) }));
    for (const t of customThemes) {
        if (merged.find((m) => m.slug === t.slug)) continue;
        merged.push({ ...t, hidden: hiddenCollections.has(t.slug), image: getThemeImage(t.slug) });
    }
    return merged;
}

export async function hideItem(kind, slug, parent = null) {
    await axios.post(`${API}/catalog/hide`, { kind, slug, parent });
    await refreshDynamicCategories();
}
export async function unhideItem(kind, slug, parent = null) {
    await axios.post(`${API}/catalog/unhide`, { kind, slug, parent });
    await refreshDynamicCategories();
}
